import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Icon } from "@iconify/react";

gsap.registerPlugin(ScrollTrigger);

export default function RemarkSection() {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);
  const paragraphsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Fade in and slide up section
      gsap.from(sectionRef.current, {
        opacity: 0,
        y: 100,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Image smooth entrance
      gsap.from(imageRef.current, {
        x: -100,
        opacity: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: imageRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      });

      // Heading animation
      gsap.from(textRef.current, {
        x: 100,
        opacity: 0,
        duration: 1.2,
        delay: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: textRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      });

      // Paragraphs stagger animation
      gsap.from(paragraphsRef.current, {
        opacity: 0,
        y: 40,
        stagger: 0.2,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: paragraphsRef.current[0],
          start: "top 90%",
          toggleActions: "play none none reverse",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="ourproducts"
      ref={sectionRef}
      className="bg-black text-white min-h-screen flex flex-col items-center justify-center px-6 md:px-12 md:py-16 pt-0 pb-10"
    >

      <button className="bg-white/10 border border-white/20 px-4 sm:px-6 py-2 mb-6 rounded-full text-xs sm:text-sm hover:bg-white/20 transition-all flex items-center">
        <Icon
          icon="material-symbols:lightbulb-2-rounded"
          className="mr-2 text-[#F26A33] drop-shadow-[0_0_8px_#F26A33]"
          width="22"
          height="22"
        />
        OUR PRODUCT

      </button>

      {/* Title */}
      <h2
        ref={textRef}
        className="text-3xl md:text-5xl font-semibold text-center mb-3"
      >
        Remark - Dubai’s Smart AI Career & Hiring Platform
      </h2>
      <p className="text-gray-300 text-center mb-12">
        Connecting Dubai’s Talent with AI-Driven Jobs
      </p>


      {/* Left Info Card */}
      <div className="max-w-screen-xl w-full flex flex-col md:flex-row items-center gap-10 2xl:-ml-130 xl:-ml-0 lg:-mr-20 z-10 lg:absolute lg:pt-24 xl:pt-0">
        <div
          ref={textRef}
          className="bg-gray-900/80 p-6 xl:md:p-8 rounded-2xl shadow-lg border border-gray-700 xl:max-w-md max-w-sm "
        >
          <p className="text-gray-200 xl:text-sm text-xs  leading-relaxed">
            Remark is not just another job portal, it’s one of the region’s advanced AI-powered hiring platforms. Built for Dubai’s competitive workforce, Remark connects employers, recruiters, and job seekers through intelligent automation, verified profiles, and smart matching.          </p>
          <p className="text-gray-200 xl:text-sm text-xs  leading-relaxed mt-4">
            From AI-driven job matches to automated resumes and interview scheduling, every feature is designed to reduce hiring time, improve accuracy, and eliminate guesswork, making recruitment seamless across the UAE.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="2xl:max-w-screen-xl xl:max-w-screen-lg max-w-screen-md flex flex-col md:flex-row items-center gap-10">
        {/* Right Image */}
        <div
          ref={imageRef}
          className="rounded-xl overflow-hidden border border-gray-700 shadow-2xl xl:w-full mx-auto "
        >
          <img
            src="https://api.visko.group/public/assets/img/uploads/ticket-docs/1772084582_24c1bb1128602f75bfba.jpg"
            alt="Remark Job Portal Preview"
            className="w-full "
          />
        </div>
      </div>
      <div ref={imageRef} className="xl:max-w-screen-xl w-full flex flex-col md:flex-row justify-end items-center gap-10 2xl:ml-75 xl:ml-11 lg:mr-32 z-10 lg:absolute lg:pt-24 xl:pt-0 ">
        <img
          src="https://api.visko.group/public/assets/img/uploads/ticket-docs/1772085119_e0ad191f9df392b561d4.jpg"
          alt="Remark Job Portal Preview"
          className="xl:h-96 rounded-xl"
        />
      </div>


      {/* Download Buttons */}
      <div
        ref={paragraphsRef}
        className="flex flex-wrap justify-center gap-4 mt-12"
      >
        {/* Play Store */}
        <a href="https://play.google.com/store/apps/details?id=com.remarkhr.dubaijobs" className="relative flex items-center gap-3 px-10 py-1.5 bg-black border border-gray-600 rounded-xl hover:bg-gray-900 transition-all group">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 opacity-30 blur-md group-hover:opacity-60 transition"></div>
          <Icon icon="logos:google-play-icon" width="28" />
          <div className="flex flex-col text-left z-10">
            <span className="text-xs text-gray-300">GET IT ON</span>
            <span className="text-sm font-semibold">Google Play</span>
          </div>
        </a>

        {/* App Store */}
        {/* <a href="https://apps.apple.com/in/app/remark-job-recruiter-app/id6475642568" className="relative flex items-center gap-3 px-10 py-1.5 bg-black border border-gray-600 rounded-xl hover:bg-gray-900 transition-all group">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-gray-500 to-gray-300 opacity-30 blur-md group-hover:opacity-60 transition"></div>
          <Icon icon="ic:baseline-apple" width="30" />
          <div className="flex flex-col text-left z-10">
            <span className="text-xs text-gray-300">Download on the</span>
            <span className="text-sm font-semibold">App Store</span>
          </div>
        </a> */}
      </div>
    </section>
  );
}
